/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { useAppSnackbarActions } from "../Components/AppSnackbarContext";
import AppSnackbarItem from "../Components/AppSnackbarContext/AppSnackbarItem";
import HttpRequestError from "../Services/HttpRequestError";
import HttpClient, { Headers, HttpClientOptions, HttpMethod } from "../Utils/HttpClient";
import { parseUrlArguments, UrlArguments } from "./UrlArguments";

export default abstract class ApiClient {
    private readonly _httpClient: HttpClient;
    private readonly _pushResponseErrors;
    private readonly _pushNetworkError;
    private readonly _push;

    protected readonly url: string;

    protected constructor(_url: string, _options?: HttpClientOptions) {
        this._httpClient = new HttpClient(_options);
        this.url = _url;
        const { pushResponseErrors, pushNetworkError, push } = useAppSnackbarActions();
        this._pushResponseErrors = pushResponseErrors;
        this._pushNetworkError = pushNetworkError;
        this._push = push;
    }

    protected post<TData>(path: string, body?: TData, headers?: Headers, customRequestName?: string, ignoreErrors?: boolean): Promise<Response> {
        return this.sendWithBody("POST", path, body, headers, customRequestName, ignoreErrors);
    }

    protected put<TData>(path: string, body?: TData, headers?: Headers, customRequestName?: string, ignoreErrors?: boolean): Promise<Response> {
        return this.sendWithBody("PUT", path, body, headers, customRequestName, ignoreErrors);
    }

    protected async get(path: string, urlArguments?: UrlArguments, ignoreErrors?: boolean): Promise<Response> {
        try {
            const urlQueryString = urlArguments
                ? `?${new URLSearchParams(parseUrlArguments(urlArguments))}`
                : "";
            const url = `${this.url}/${path}${urlQueryString}`
            const response = await this._httpClient.get(url);
            if (response.ok) {
                return response;
            }

            if (ignoreErrors) {
                return Promise.reject(response.text());
            }

            throw new HttpRequestError(url, response);
        } catch (e) {
            if (!(e instanceof HttpRequestError)) {
                this._pushNetworkError();
            }

            throw e;
        }
    }

    protected async delete(path: string, customRequestName?: string): Promise<Response> {
        try {
            const url = `${this.url}/${path}`
            const response = await this._httpClient.delete(url);
            if (response.ok) {
                return response;
            }

            throw new HttpRequestError(customRequestName ?? url, response);
        } catch (e) {
            if (!(e instanceof HttpRequestError)) {
                this._pushNetworkError();
            }

            throw e;
        }
    }

    protected push(items: AppSnackbarItem[]) {
        this._push(items);
    }

    protected async sendWithBody<TData>(httpMethod: HttpMethod, path: string, body?: TData, headers?: Headers, customRequestName?: string, ignoreErrors?: boolean): Promise<Response> {
        try {
            const url = `${this.url}/${path}`
            const response = await (httpMethod === "POST" ? this._httpClient.post(url, body, headers) : this._httpClient.put(url, body, headers));
            if (response.ok) {
                return response;
            }

            if (ignoreErrors) {
                return Promise.reject(await response.text());
            }

            throw new HttpRequestError(customRequestName ?? url, response);
        } catch (e) {
            if (!(e instanceof HttpRequestError)) {
                this._pushNetworkError();
            }

            throw e;
        }
    }
}
